import {
  fetchWidgetTheme,
  fetchInstallWidgetTheme,
  baseUrl,
} from '~/lib/streamlabs-api';

import { until } from '@vueuse/core';
import Bindings, { openBrowserTo } from '~/lib/bindings';
import { getConfigFromPaths } from '~/lib/themes/install-theme';
import {
  createUniqueSceneCollection,
  Source,
  Scene,
  Transition,
} from '~/lib/themes/obs';
import { sleep } from '~/lib/util';
import type { ETransitionType } from '~/lib/themes/nodes/transition';

type SortValues = 'total_installs' | 'created_at' | 'recent_installs';
export type TypeValues = 'all' | 'overlay' | 'widget'; // | "page";

type SEOverlay = {
  current_program_scene: string;
  current_scene: string;
  groups: string[];
  name: string;
  scene_order: { name: string }[];
  sources: any[];
  transition_duration: number;
  transitions: { name: string; type: ETransitionType }[];
};

export const useLibraryStore = defineStore('library', () => {
  const dialogs = useDialogsStore();
  const api = useApiStore();
  const auth = useAuthStore();
  const app = useAppStore();

  const { public: $env } = useRuntimeConfig();
  const { queryParamToComputed } = useQueryParamToComputed();

  const isInstalling = ref(false);
  const installProgress = ref(0);
  const installStepLabel = ref('Warming up');

  const SEToken = ref('');

  const setInstallProgress = (progress: number, label: string) => {
    installProgress.value = progress;
    installStepLabel.value = label;
  };

  const detectSEPlugin = async () => {
    if (SEToken.value) return;

    const resp = await Bindings.qt.getCookieValue(
      'StreamElementsBrowserWidget',
      'id_token_creator',
    );
    if (resp.value) {
      SEToken.value = resp.value;
    }
  };

  const openSEImport = async () => {
    if (!SEToken.value) return;
    // TODO: should we return if login token is null here?
    const loginToken = await auth.getLoginToken();
    const importPath = `import/streamelements?jwt=${SEToken.value}&origin=obs-plugin`;
    openBrowserTo(
      `${baseUrl.value}/slobs/magic/dashboard?login_token=${loginToken}&r=${encodeURIComponent(importPath)}`,
    );
  };

  const installOverlayFileUrl = async (
    url: string,
    sceneCollectionName: string,
    beforeRestart?: () => unknown,
  ) => {
    console.groupCollapsed('installOverlayFileUrl');
    console.debug('installOverlayFileUrl', url);
    setInstallProgress(40, 'Downloading overlay config');

    const paths = await Bindings.fs.downloadZip(url);

    console.debug({ paths });

    setInstallProgress(50, 'Parsing config paths');

    const { config, configPath, contents } = await getConfigFromPaths(paths);

    console.debug({ configPath, config });

    setInstallProgress(60, 'Creating scene collection');

    // Create a fresh scene collection to load into
    await createUniqueSceneCollection(sceneCollectionName);

    setInstallProgress(80, 'Loading overlay sources');

    await config.load({ assetsPath: configPath.slice(0, -12), contents });

    setInstallProgress(100, 'All done!');

    await sleep(1000);

    isInstalling.value = false;

    if (beforeRestart) {
      await beforeRestart();
    }

    dialogs.showDialog('RESTART_OBS');

    await Bindings.obs.toggle_hide_self(false);

    console.groupEnd();
  };

  const installSEOverlays = async (collections: SEOverlay[]) => {
    console.groupCollapsed('installSEOverlay');
    setInstallProgress(0, 'Initializing');

    isInstalling.value = true;

    // Ensure window is refocused after redirecting to browser
    app.bringToFront();

    await Bindings.obs.toggle_hide_self(true);

    for (const collection of collections) {
      setInstallProgress(20, `Creating scene collection ${collection.name}`);

      await createUniqueSceneCollection(collection.name);

      setInstallProgress(40, `Loading scenes for ${collection.name}`);

      const scenes = collection.sources.filter(
        (source) => source.id === 'scene',
      );

      for (const scene of scenes) {
        const s = await Scene.create(scene.name);
        await s.makeActive();
        setInstallProgress(
          60,
          `Adding sources for ${collection.name} - ${scene.name}`,
        );
        for (const item of [...scene.settings.items].reverse()) {
          const source = collection.sources.find(
            (source) => source.uuid === item.source_uuid,
          );
          let obsSource = (await Source.all()).find(
            (existing) => existing.name === source.name,
          );

          if (!obsSource?.name) {
            // Download the source locally if its animated media
            if (source.id === 'ffmpeg_source') {
              console.log(source.settings);
              const mediaUrl = source.settings.input;
              const fileName = mediaUrl.match(/[^/]+$/)[0];
              const filePath = await Bindings.fs.downloadFile(
                mediaUrl,
                fileName,
              );
              source.settings.is_local_file = true;
              // fs_downloadFile returns an array of character codes representing the path
              source.settings.local_file = filePath.path
                .map((c) => String.fromCharCode(c))
                .join('');
            }
            obsSource = await Source.create(
              source.id,
              source.name,
              source.settings,
            );
          }

          const existingSceneItem = await s.findSource(obsSource.name);
          if (!existingSceneItem) {
            const sceneItem = await s.addSource(obsSource);
            await sceneItem.setCrop({
              top: item.crop_top,
              left: item.crop_left,
              right: item.crop_right,
              bottom: item.crop_bottom,
            });
            await sceneItem.setPos(item.pos.x, item.pos.y);
            await sceneItem.setScale(item.scale.x, item.scale.y);
          }
        }
      }

      const activeScene = (await Scene.all()).find(
        (scene) => scene.name === collection.current_scene,
      );

      await activeScene?.makeActive();

      setInstallProgress(80, `Loading transitions from ${collection.name}`);

      if (collection.transitions.length > 0) {
        for (const transition of collection.transitions) {
          Transition.create(transition.type, transition.name);
        }
      }
    }

    setInstallProgress(100, 'All done!');

    await sleep(1000);

    isInstalling.value = false;

    await Bindings.obs.toggle_hide_self(false);

    console.groupEnd();
  };

  const carouselOverlays = useDeferredAsyncData(
    'carousel-overlays',
    async () => {
      const { data } = await api.get<CarouselItem[]>(
        `${$env.MARKETPLACE_ORIGIN}/api/v2/dashboard/carousel?type=overlays`,
      );

      return data;
    },
  );

  const dashboardOverlays = useDeferredAsyncData(
    'dashboard-overlays',
    async () => {
      const { data } = await api.get<ResponseDashboard>(
        `${$env.MARKETPLACE_ORIGIN}/api/v2/dashboard?type=overlays`,
      );

      return data;
    },
  );

  const trendingCollections = useDeferredAsyncData(
    'trending-collections',
    async () => {
      const { data } = await api.get<{
        current_page: number;
        data: LibraryCollection[];
      }>(
        `${$env.MARKETPLACE_ORIGIN}/api/v2/collections?location=advanced_search`,
      );

      return data.data;
    },
  );

  const collections = useDeferredAsyncData('collections', async () => {
    const { data } = await api.get<{
      current_page: number;
      data: LibraryCollection[];
    }>(`${$env.MARKETPLACE_ORIGIN}/api/v2/collections`);

    return data.data;
  });

  const fetchItem = async (itemId: number | string) => {
    return await api.get<TsThemesItemWidget | TsThemesItemOverlay>(
      `${$env.MARKETPLACE_ORIGIN}/api/v2/themes/${itemId}`,
    );
  };

  const fetchBundledItems = async (itemId: number | string) => {
    return await api.get<
      Omit<TsThemesItemWidget | TsThemesItemOverlay, 'creator'>[]
    >(`${$env.MARKETPLACE_ORIGIN}/api/v2/themes/${itemId}/bundle`);
  };

  const installOverlay = async (
    overlay: TsThemesItemOverlay,
    params = {},
    beforeRestart?: () => unknown,
  ) => {
    console.groupCollapsed('installOverlay');
    if (
      !overlay.is_free &&
      !auth.ensureUltra('overlay_install', {
        refl: 'obsplugin-theme-library',
        source: 'library-overlays',
        ref_item: overlay.object.id,
        aff_id: overlay.creator.aff_id,
        ...params,
      })
    ) {
      return;
    }

    setInstallProgress(0, 'Initializing');

    isInstalling.value = true;

    setInstallProgress(20, 'Fetching overlay info');

    const {
      data: {
        package: {
          overlays: { default: overlayFileUrl },
        },
      },
    } = await api.post<{
      type: 'overlay';
      package: {
        overlays: {
          default: string;
          [locale: string]: string;
        };
      };
    }>(`${$env.MARKETPLACE_ORIGIN}/api/v2/themes/${overlay.id}/install`);

    try {
      await Bindings.obs.toggle_hide_self(true);
    } catch (err) {
      console.error(err);
    }

    console.debug({ overlayFileUrl });

    await installOverlayFileUrl(overlayFileUrl, overlay.name, beforeRestart);
    console.groupEnd();
    return true;
  };

  const installWidgetTheme = async (
    widgetTheme: TsThemesItemWidget,
    widgets?: TsWidgetType[],
    params = {},
  ) => {
    if (!widgetTheme.is_free) {
      if (
        !auth.ensureUltra('widget_theme_install', {
          refl: 'obsplugin-widget-library',
          source: 'library-widget-themes',
          ref_item: widgetTheme.object.id,
          aff_id: widgetTheme.creator.aff_id,
          ...params,
        })
      ) {
        return;
      }
    }

    const { data: legacyWidgetTheme } = await fetchWidgetTheme(
      widgetTheme.object.id,
    );

    if (!legacyWidgetTheme.value) {
      return;
    }

    await fetchInstallWidgetTheme(
      legacyWidgetTheme.value,
      widgets && widgets.length ? (widgets as WidgetThemeType[]) : undefined,
    );
    return true;
  };

  const page = ref(1);
  const hasMoreItems = ref(true);
  const perPage = ref(30);

  const items = ref<LibraryItem[]>([]);

  const typeOptions: { [key in TypeValues]: string } = {
    all: 'All Types',
    overlay: 'Overlays',
    widget: 'Widget Themes',
  };

  const type = queryParamToComputed<TypeValues>('type', {
    set: (v) => (v === 'all' ? undefined : v),
    get: (v) => v || 'all',
  });

  const sortOptions: { [key in SortValues]: string } = {
    total_installs: 'Most Installed',
    created_at: 'Newest',
    recent_installs: 'Trending',
  };

  const sort = queryParamToComputed<SortValues | ''>('sort');

  const order = ref<'asc' | 'desc' | undefined>('desc');
  const price = ref<'free' | 'ultra' | undefined>();

  const selectedColors = queryParamToComputed<string[]>('colors', {
    get: (v) => (v ? (Array.isArray(v) ? v : [v]) : []),
  });

  const keywords = computed(() => {
    return selectedColors.value;
  });

  const q = queryParamToComputed('q');

  const fetchPage = async (page: number, libraryType: LibraryType) => {
    const url = new URL(`${$env.MARKETPLACE_ORIGIN}/api/v2/themes`);

    // if (collection.value) {
    //   url.searchParams.set("collection", collection.value);
    // }

    if (libraryType) {
      url.searchParams.set('type', libraryType);
    } else if (type.value && type.value !== 'all') {
      url.searchParams.set('type', type.value);
    }

    if (sort.value) {
      url.searchParams.set('sort', sort.value);
    }

    if (order.value) {
      url.searchParams.set('order', order.value);
    }

    if (perPage.value) {
      url.searchParams.set('per_page', perPage.value.toString());
    }

    if (price.value) {
      url.searchParams.set('price', price.value);
    }

    if (q.value) {
      url.searchParams.set('search', q.value);
    }

    if (keywords.value.length > 0) {
      for (const i in keywords.value) {
        url.searchParams.set(`keywords[${i}]`, keywords.value[i] || '');
      }
    }

    url.searchParams.set('page', page.toString());

    //   await sleep(30000);
    return await api.get<{
      data: LibraryItem[];
      meta: {
        current_page: number;
        from: number;
        last_page: number;
        links: {
          url: string | null;
          label: string;
          active: boolean;
        }[];
        path: string;
        per_page: number;
        to: number;
        total: number;
      };
    }>(url.toString());
  };

  const placeholderAndFetchNextPage = async ({
    type,
  }: {
    type: LibraryType;
  }) => {
    if (!hasMoreItems.value) {
      return;
    }

    hasMoreItems.value = false;

    items.value.push(...new Array(perPage.value).fill(undefined));

    const result = await fetchPage(page.value++, type);

    const fetchedItems = result.data?.data;

    items.value.splice(-perPage.value);

    if (fetchedItems) {
      items.value.push(...fetchedItems);
    } else {
      hasMoreItems.value = false;
      await until(hasMoreItems).toBe(true);
    }

    hasMoreItems.value =
      !!result.data?.meta.last_page && page.value < result.data?.meta.last_page;

    return result;
  };

  return {
    carouselOverlays,
    dashboardOverlays,
    trendingCollections,
    collections,

    fetchItem,
    fetchBundledItems,

    detectSEPlugin,
    openSEImport,

    installOverlay,
    installWidgetTheme,
    installSEOverlays,

    isInstalling,
    installProgress,
    installStepLabel,
    SEToken,

    selectedColors,
    keywords,
    q,
    sort,
    sortOptions,
    type,
    typeOptions,
    page,
    hasMoreItems,
    perPage,
    items,

    placeholderAndFetchNextPage,
  };
});
